/**
 * SettingsPageHeader - Standardized header for all settings pages.
 * Renders an icon box, title, description, and optional action buttons
 * in a consistent layout across every settings page.
 */

import type { LucideIcon } from 'lucide-react'

interface SettingsPageHeaderProps {
  /** Lucide icon component displayed in the colored icon box */
  icon: LucideIcon

  /** Small contextual eyebrow displayed above the page title */
  /** Defaults to a generic settings label when not overridden */
  eyebrow?: string

  /** Page title rendered as an h2 */
  title: string

  /** Short description rendered below the title */
  description: string

  /** Optional action buttons rendered on the right side */
  actions?: React.ReactNode
}

export function SettingsPageHeader({
  icon: Icon,
  eyebrow = 'Settings',
  title,
  description,
  actions
}: SettingsPageHeaderProps) {
  return (
    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="type-eyebrow text-primary/80">{eyebrow}</p>
          <h2 className="type-page-title mt-1 text-foreground">{title}</h2>
          <p className="type-body mt-1.5 max-w-2xl text-muted-foreground">{description}</p>
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}
